import { useActionPanelController } from '@dealstack/frontend/investor/pages/Valuations/useActionPanelController';
import { INVESTOR_WORKFLOW } from '@dealstack/frontend/investor/pages/Valuations/Valuations.utils';
import { InvestorDocumentsDrawer } from '@dealstack/frontend/investor/pages/Valuations/ValuationsDrawer/InvestorDocumentsDrawer';
import { RequirementsInstructionsComponent } from '@dealstack/frontend/investor/pages/Valuations/ValuationsDrawer/RequirementsInstructionsComponent';
import { isSecurityIssuedInUnits } from '@dealstack/frontend/shared/cap.ts';
import { ActionCard } from '@dealstack/frontend/shared/components/ActionCard/ActionCard';
import { AssetsLoader } from '@dealstack/frontend/shared/components/AssetsLoader/AssetsLoader';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { formatNumber } from '@dealstack/shared/lib/format/number';
import { LONG_DASH } from '@dealstack/frontend/shared/other/react/specialSigns';
import { SHAREHOLDER_INVESTMENT_STATUS } from '@dealstack/shared/lib/common/business/shareholder';
import { DATE_FORMATS, formatDateToUI } from '@dealstack/shared/lib/format/dates/date';
import { Box, em, rem, Text } from '@mantine/core';
import { useCallback } from 'react';
import styled from 'styled-components';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types';
import { ISecurities } from './useActionPanelController.types';
import { VALUATIONS_DRAWER_TYPE } from './useValuationsController';
import { BankingDetailsComponent } from './ValuationsDrawer/BankingDetailsComponent';

const CARD_TYPE = {
	SDW: 'SDW',
	INVESTMENT: 'INVESTMENT',
};

interface Props {
	className?: string;
	hasAllInvestmentsOption: boolean;
}
const EMPTY_SECURITY_TYPE_PLACEHOLDER = '';
const THREE_DOTS = '...';
const computeDate = (date?: string) => {
	return date ? formatDateToUI(new Date(date), DATE_FORMATS.CALENDAR_DATE) : LONG_DASH;
};
const ActionPanel = ({ className, hasAllInvestmentsOption }: Props) => {
	const tInvestor = useTranslationInvestor();

	const {
		panelRef,
		actionPanelData,
		onNonSDWDocumentClick,
		onSDWDocumentClick,
		isLoading,
		currentDrawer,
		openDrawer,
		onDrawerClose,
		bankingDetails,
		whatDoINeedInstruction,
		whatDoINeedInstructionFileName,
		selectedActionCardInvestorId,
		setSelectedActionCardInvestorId,
	} = useActionPanelController(hasAllInvestmentsOption);

	const getMappedInvestorWorkflow = useCallback((investorWorkflow: INVESTOR_WORKFLOW) => {
		const mapped = {
			[INVESTOR_WORKFLOW.KYC]: tInvestor('valuations.action_panel.type.kyc'),
			[INVESTOR_WORKFLOW.DOCUMENT]: tInvestor('valuations.action_panel.type.document'),
			[INVESTOR_WORKFLOW.JOINER_CREATE]: tInvestor(
				'valuations.action_panel.type.new_investment'
			),
			[INVESTOR_WORKFLOW.JOINER]: tInvestor('valuations.action_panel.type.new_investment'),
			[INVESTOR_WORKFLOW.ALLOCATION]: tInvestor('valuations.action_panel.type.allocation'),
			[INVESTOR_WORKFLOW.LEAVER]: tInvestor('valuations.action_panel.type.transfer'),
		};

		return mapped[investorWorkflow];
	}, []);

	const STATUS_TEXT = {
		[SHAREHOLDER_INVESTMENT_STATUS.PENDING_APPROVAL]: tInvestor(
			'valuations.action_panel.status.pending_approval'
		),
		[SHAREHOLDER_INVESTMENT_STATUS.COMPLETED]: tInvestor(
			'valuations.action_panel.status.completed'
		),
		[SHAREHOLDER_INVESTMENT_STATUS.NEW_OFFER]: tInvestor(
			'valuations.action_panel.status.new_offer'
		),
		[SHAREHOLDER_INVESTMENT_STATUS.NEEDS_ATTENTION]: tInvestor(
			'valuations.action_panel.status.needs_attention'
		),
		[SHAREHOLDER_INVESTMENT_STATUS.PENDING]: tInvestor(
			'valuations.action_panel.status.pending'
		),
	};

	const displayBottomSection = useCallback(
		(status: SHAREHOLDER_INVESTMENT_STATUS, cardType: string) => {
			if (
				status === SHAREHOLDER_INVESTMENT_STATUS.PENDING_APPROVAL ||
				status === SHAREHOLDER_INVESTMENT_STATUS.COMPLETED ||
				cardType === CARD_TYPE.SDW
			) {
				return;
			}

			return {
				text: tInvestor('valuations.action_panel.button_name'),
				action: () => {
					openDrawer(VALUATIONS_DRAWER_TYPE.REQUIREMENTS_INSTRUCTIONS);
				},
				icon: 'information-filled' as PredefinedIcons,
			};
		},
		[]
	);

	const formatSecuritySubtitle = useCallback(
		(securities: ISecurities[]): JSX.Element | undefined => {
			const firstSecurity = securities[0] ?? {};
			let string = formatSecurityValueWithTranslation(firstSecurity);

			if (securities?.length > 1) {
				string += THREE_DOTS;
			}
			return <span key={string}>{string}</span>;
		},
		[]
	);

	const formatSecurityTooltipText = useCallback(
		(securities: ISecurities[]): Array<JSX.Element | undefined> | undefined => {
			if (securities.length <= 1) {
				return;
			}

			return securities.map((security) => {
				const formatted = formatSecurityValueWithTranslation(security);
				return <div key={formatted}>{formatted}</div>;
			});
		},
		[]
	);
	const formatSecurityValueWithTranslation = (security: ISecurities) => {
		const issuedInUnits = isSecurityIssuedInUnits(security.issuedIn);
		const value = issuedInUnits
			? formatNumber({ value: security.unit, returnNumericalZero: true })
			: formatNumber({
					value: security.cost,
					currency: security.currency,
					returnNumericalZero: true,
				});

		return tInvestor('valuations.action_panel.shares_allocated_value', {
			share_number: value,
			security_type: EMPTY_SECURITY_TYPE_PLACEHOLDER,
			security_name: security?.name,
		});
	};
	const computeElements = useCallback(
		(cardType: string, isLeaver: boolean, securities?: ISecurities[]) => {
			if (!securities) {
				return;
			}

			const title = tInvestor(
				`valuations.action_panel.${isLeaver ? 'shares_being_sold' : 'shares_allocated'}`
			);

			const computedStringByCardType = {
				[CARD_TYPE.INVESTMENT]: {
					secondTitle: title,
					secondSubtitle: formatSecuritySubtitle(securities),
					tooltipText: formatSecurityTooltipText(securities),
				},
			};

			return computedStringByCardType[cardType];
		},
		[]
	);

	const emptyCardImageSrc = '/context/investor/valuation/empty_box.png';

	return (
		<div className={className} ref={panelRef}>
			<Text
				size="H5Bold"
				style={{ marginBottom: '1rem', color: defaultTheme.v3.colors.gray[8] }}
			>
				{tInvestor('valuations.action_panel.title')}
			</Text>
			<Box>
				{isLoading && <InlineLoader />}
				{actionPanelData?.length ? (
					<ScrollableSection>
						{actionPanelData.map((item, index) => {
							const cardType = item.isSDWDocument
								? CARD_TYPE.SDW
								: CARD_TYPE.INVESTMENT;

							return (
								<ActionCard
									key={`${item.investorId}-${index}`}
									title={tInvestor('valuations.action_panel.company_name')}
									subtitle={item.companyName}
									{...computeElements(
										cardType,
										item.investorWorkflow === INVESTOR_WORKFLOW.LEAVER,
										item.security
									)}
								>
									<ActionCard.CardHeader
										type={getMappedInvestorWorkflow(item.investorWorkflow)}
										status={item.status}
										text={STATUS_TEXT[item.status]}
									/>
									<ActionCard.TagPair />
									{(item.isSDWDocument || item.hasWorkflow || item.showBank) && (
										<ActionCard.CardButton
											showBank={item.showBank}
											linkText={
												item.linkText ? tInvestor(item.linkText) : undefined
											}
											onClick={() => {
												setSelectedActionCardInvestorId(item.investorId);
												if (item.isSDWDocument) {
													onSDWDocumentClick(
														item.investorId,
														item.dealId,
														item.mipId
													);
													return;
												}
												if (item.hasWorkflow) {
													onNonSDWDocumentClick(item.link);
													return;
												}
												openDrawer(VALUATIONS_DRAWER_TYPE.BANKING_DETAILS);
											}}
										/>
									)}

									<ActionCard.CardBottomSection
										date={computeDate(item.createdAt)}
										bottomRight={displayBottomSection(item.status, cardType)}
									/>
								</ActionCard>
							);
						})}
					</ScrollableSection>
				) : (
					<ImageWrapper>
						<AssetsLoader src={emptyCardImageSrc} />
						<Text size="H5Bold" mb="2rem">
							{tInvestor('valuations.action_panel.no_action_pending')}
						</Text>
					</ImageWrapper>
				)}
			</Box>

			<InvestorDocumentsDrawer
				opened={currentDrawer === VALUATIONS_DRAWER_TYPE.REQUIREMENTS_INSTRUCTIONS}
				onClose={onDrawerClose}
				titleText={tInvestor('valuations.drawer.what_do_i_need.title')}
			>
				<RequirementsInstructionsComponent
					instruction={whatDoINeedInstruction}
					fileName={whatDoINeedInstructionFileName}
				/>
			</InvestorDocumentsDrawer>

			<InvestorDocumentsDrawer
				opened={
					currentDrawer === VALUATIONS_DRAWER_TYPE.BANKING_DETAILS &&
					!!selectedActionCardInvestorId
				}
				onClose={onDrawerClose}
				titleText={tInvestor('valuations.drawer.banking_details.title')}
			>
				<BankingDetailsComponent {...bankingDetails} />
			</InvestorDocumentsDrawer>
		</div>
	);
};
const ImageWrapper = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	color: ${defaultTheme.v3.colors.gray[8]};
	gap: 1rem;
	img {
		width: ${rem(160)};
		height: ${rem(160)};
	}
`;

const ScrollableSection = styled.div`
	display: flex;
	width: 100%;
	gap: 1rem;
`;

const Styled = styled(ActionPanel)`
	width: 100%;
	min-height: ${rem(272)};
	padding: 1rem;
	border-radius: 0.5rem;
	background-color: ${defaultTheme.v3.colors.dark[8]};
	overflow-x: auto;
	overflow-y: hidden;
	--scroll-bar-color: ${defaultTheme.v2.color.neutral[400]};
	::-webkit-scrollbar {
		height: ${em(10)};
	}

	::-webkit-scrollbar-thumb {
		border: 1px solid var(--scroll-bar-color);
		background: var(--scroll-bar-color);
		border-radius: 0.25rem;
	}
`;

export { Styled as ActionPanel };
